import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICreateActivo,
  IQueryParam,
  IUpdateActivo,
  IActivo,
  ITracker,
  IDashcam,
  IFilter,
  ICategoriaActivo,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { CrearEditarTrackerComponent } from '../../../administracion/trackers/crear-editar-tracker/crear-editar-tracker.component';
import {
  CrearEditarDashcamComponent,
  ICrearEditarDashcamData,
} from '../../../administracion/dashcams/crear-editar-dashcam/crear-editar-dashcam.component';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ChequeoIdentificacion } from '../../../../../auxiliares/servicios/chequeo-identificacion';
import {
  revalidarTrackerAlCambiarDashcam,
  trackerODashcamRequerido,
} from '../../../../../auxiliares/tracker-o-dashcam.util';

@Component({
  selector: 'app-crear-editar-activo',
  templateUrl: './crear-editar-activo.component.html',
  styleUrl: './crear-editar-activo.component.scss',
  standalone: false,
})
export class CrearEditarActivoComponent implements OnInit, OnDestroy {
  data = inject<{
    edit?: IActivo;
    categoria: ICategoriaActivo;
  }>(MAT_DIALOG_DATA);
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private service = inject(ActivosService);
  dialogRef = inject<MatDialogRef<CrearEditarActivoComponent>>(MatDialogRef);
  paramsService = inject(ParamsService);

  public clientePropio = LoginService.getCliente();
  public loading = false;
  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;

  ////////////////////////////////////////////////////////////////////////////
  public trackers?: ITracker[] = [];
  public trackers$?: Subscription;
  public dashcams?: IDashcam[] = [];
  public dashcams$?: Subscription;
  private idCliente$?: Subscription;
  public tipoCliente = HelperService.getTipoCliente();

  get mostrarCamposCliente(): boolean {
    return this.tipoCliente !== 'Final';
  }

  get idCliente() {
    return this.form?.get('idCliente')?.value;
  }

  ///////////////////////////////////////////////////////////////////////
  //FORM

  public chequeo = new ChequeoIdentificacion<IActivo>({
    entidad: 'activo',
    articulo: 'un',
    campo: 'identificacion',
    control: () => this.form?.get('identificacion'),
    listar: (query) => this.service.getFiltered(query),
    idCliente: () => this.idCliente,
    excluirId: () => this.data?.edit?._id,
    filtroExtra: () => ({
      categoria: this.data?.edit?.categoria || this.data?.categoria || 'Normal',
    }),
  });

  private createForm() {
    const categoria: ICategoriaActivo = this.data.categoria || 'Normal';

    // Determinar el cliente por defecto basado en el tipo de usuario
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : (this.data?.edit?.idCliente ?? this.clientePropio?._id);

    this.form = new FormGroup({
      identificacion: new FormControl(
        this.data?.edit?.identificacion,
        Validators.required,
        this.chequeo.validador,
      ),
      categoria: new FormControl(
        this.data?.edit?.categoria || categoria,
        Validators.required,
      ),
      idCliente: new FormControl(clienteDefault, Validators.required),
      // Obligatorio solo al crear: al editar hay que poder sacarlo para
      // liberar el tracker y asignarlo a otro activo o vehículo. Con una
      // dashcam elegida deja de ser obligatorio: alcanza con una de las dos.
      idTracker: new FormControl(
        this.data?.edit?.idTracker,
        this.data?.edit?._id ? [] : trackerODashcamRequerido,
      ),
      idDashcam: new FormControl(this.data?.edit?.idDashcam),
      idsClientesQuePuedenAtender: new FormControl(
        this.data?.edit?.idsClientesQuePuedenAtender ?? null,
      ),
      idsClientesQuePuedenAtenderEventosTecnicos: new FormControl(
        this.data?.edit?.idsClientesQuePuedenAtenderEventosTecnicos ?? null,
      ),
      configHorariosAtencion: new FormControl(
        this.data?.edit?.configHorariosAtencion ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      configHorariosAtencionTecnica: new FormControl(
        this.data?.edit?.configHorariosAtencionTecnica ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      puedeSolicitarServicioTecnico: new FormControl(
        this.data?.edit?.puedeSolicitarServicioTecnico ?? false,
      ),
      idGrupo: new FormControl(this.data?.edit?.idGrupo),
    });
  }

  ///////////////////////////////////////////////////////////////////////////
  //LISTAR
  public async listarTrackers() {
    if (this.form?.value?.idCliente) {
      const filter: IFilter<ITracker> = {
        $and: [
          { idCliente: this.form?.value?.idCliente },
          {
            $or: [
              {
                asignadoA: null,
              },
              {
                asignadoA: { $exists: false },
              },
            ],
          },
        ],
      };
      if (this.data?.edit?._id) {
        filter.$and[1].$or.push({
          _id: this.data?.edit?.idTracker,
        });
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: 'identificacion',
        limit: 0,
        includeChildren: true,
      };
      //
      this.trackers$?.unsubscribe();
      this.trackers$ = this.lista2.trackers(query, 30).subscribe((data) => {
        const trackers = JSON.parse(JSON.stringify(data.datos));
        this.trackers = trackers;
        // Re-aplica el valor para que ng-select encuentre el item en la lista cargada
        const idTracker = this.form.get('idTracker')?.value;
        if (idTracker) {
          this.form.get('idTracker')?.setValue(idTracker, { emitEvent: false });
        }
      });
    }
  }

  /** Dashcams del cliente sin asignar, más la del activo en edición. */
  public listarDashcams(): void {
    const idCliente = this.form?.value?.idCliente;
    this.dashcams$?.unsubscribe();
    if (!idCliente) {
      this.dashcams = [];
      return;
    }
    const libres: IFilter<IDashcam>[] = [
      { asignadoA: null },
      { asignadoA: { $exists: false } },
    ];
    const actual = this.data?.edit?.idDashcam;
    if (actual) libres.push({ _id: actual });
    const filter: IFilter<IDashcam> = {
      $and: [{ idCliente }, { $or: libres }],
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'nombre identificacion uniqueId',
      sort: 'nombre',
      limit: 0,
      includeChildren: true,
    };
    this.dashcams$ = this.lista2.dashcams(query, 30).subscribe((data) => {
      this.dashcams = data.datos;
    });
  }

  /////////////////////////////////////////////////////////////////////////
  ///CREAR

  /** Alta de una dashcam en el cliente del activo; vuelve ya elegida. */
  public crearDashcam(): void {
    const idCliente = this.form?.value?.idCliente;
    if (!idCliente) return;
    this.formularios
      .abrir<CrearEditarDashcamComponent, ICrearEditarDashcamData, IDashcam>(
        CrearEditarDashcamComponent,
        { tamano: 'grande', data: { idCliente } },
      )
      .afterClosed()
      .subscribe((res) => {
        if (!res?._id) return;
        this.listarDashcams();
        this.form.patchValue({ idDashcam: res._id });
      });
  }

  public async crearTracker() {
    this.paramsService.setParams({
      desdeActivo: true,
      idCliente: this.form.value.idCliente,
    });

    const dialog = this.formularios.abrir(CrearEditarTrackerComponent, {
      tamano: 'grande',
    });
    dialog.afterClosed().subscribe(async (res) => {
      if (res) {
        this.form.patchValue({ idTracker: res });
        await this.listarTrackers();
      }
    });
  }

  ///////////////////////////////////////////////////////////////////////////////////
  public async onSubmit() {
    this.loading = true;
    try {
      if (!(await this.chequeo.puedeGuardar())) {
        this.helper.notifError(`${this.chequeo.textoRepetida} Elegí otra.`);
        this.loading = false;
        return;
      }

      const data = this.getData();
      if (this.data?.edit?._id) {
        // Update
        await this.service.update(this.data.edit._id, data);
        this.helper.notifSuccess('Actualizado correctamente');
      } else {
        // Create
        await this.service.create(data);
        this.helper.notifSuccess('Creado correctamente');
      }
      this.dialogRef.close(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  private async onClienteChange(idCliente: string | null) {
    // Resetea warnings de duplicados
    // El mensaje de identificación repetida aclara el cliente: revalidar.
    this.chequeo.revalidar();

    // Resetea el form
    this.form
      ?.get('idsClientesQuePuedenAtenderEventosTecnicos')
      ?.patchValue(null);

    this.form.patchValue({ idTracker: null, idDashcam: null });
    this.form.patchValue({ idGrupo: null });

    // Filtrar horarios: conservar solo clientePropio si estaba seleccionado
    const propioId = this.clientePropio?._id;
    for (const ctrl of [
      'configHorariosAtencion',
      'configHorariosAtencionTecnica',
    ]) {
      const current = this.form.get(ctrl)?.value;
      if (current?.length) {
        const filtered = current.filter((c: any) => c.idCliente === propioId);
        this.form.get(ctrl)?.patchValue(filtered.length ? filtered : null);
      }
    }

    // Carga las cosas del nuevo cliente
    if (idCliente) {
      this.listarDashcams();
      await Promise.all([this.listarTrackers()]);
    } else {
      this.trackers = [];
      this.dashcams = [];
    }
  }

  public getData(): ICreateActivo | IUpdateActivo {
    const data: ICreateActivo | IUpdateActivo = this.form?.value;
    // Sincronizar campos legacy con los nuevos al momento del submit
    const idsAtencion = (data.configHorariosAtencion ?? [])
      .map((c) => c.idCliente)
      .filter(Boolean) as string[];
    const idsTecnicos = (data.configHorariosAtencionTecnica ?? [])
      .map((c) => c.idCliente)
      .filter(Boolean) as string[];
    data.idsClientesQuePuedenAtender = idsAtencion;
    data.idsClientesQuePuedenAtenderEventosTecnicos = idsTecnicos.length
      ? idsTecnicos
      : null;
    return data;
  }

  private setTitles() {
    if (this.data?.edit?._id) {
      this.title = 'Editar Activo';
      this.subtitle = this.data?.edit?.identificacion;
    } else {
      this.title = 'Crear Activo';
    }
  }

  /////////////////////////////////////////////////////////////////////////////
  //HOOKS
  async ngOnInit() {
    this.loading = true;
    this.createForm();
    revalidarTrackerAlCambiarDashcam(this.form);
    this.setTitles();

    // Preseleccionar el cliente del permiso activo al crear
    if (!this.data?.edit) {
      this.form.patchValue({
        idCliente: this.clientePropio?._id,
      });
    }

    this.listarDashcams();
    await Promise.all([this.listarTrackers()]);

    // Suscribirse a cambios del cliente DESPUÉS de la carga inicial para no
    // disparar onClienteChange durante la resolución asíncrona del selector
    this.idCliente$ = this.form
      .get('idCliente')
      ?.valueChanges.subscribe(
        async (idCliente) => await this.onClienteChange(idCliente),
      );

    this.loading = false;
  }

  ngOnDestroy(): void {
    this.trackers$?.unsubscribe();
    this.dashcams$?.unsubscribe();
    this.idCliente$?.unsubscribe();
  }
}
